import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { useToast } from '@/hooks/use-toast';
import { useDebounce } from '@/hooks/useDebounce';
import { useCreators } from '@/hooks/useCreators';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { Badge } from '@/components/ui/badge';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { mapSupabaseError } from '@/utils/security';
import {
  Search, CheckCircle, XCircle, Clock, Shield, Loader2,
  Instagram, Youtube, Plus, User, Globe, Trash2,
} from 'lucide-react';

/**
 * Reusable "Social Media" tab. Lets any account type (talent, brand,
 * venture, agent) search for and claim their own social handles via
 * the same Scrapecreators-backed search-creators edge function used
 * elsewhere. Submissions go to claim-creator-profile and become
 * pending claim_status='pending' rows on `creators`.
 *
 * Admin still has to approve, same as the talent /claim-creator flow.
 */

const PLATFORMS = [
  { id: 'tiktok',    label: 'TikTok',    Icon: () => <span className="text-xs font-bold">TT</span> },
  { id: 'instagram', label: 'Instagram', Icon: () => <Instagram className="h-4 w-4" /> },
  { id: 'youtube',   label: 'YouTube',   Icon: () => <Youtube className="h-4 w-4" /> },
] as const;

type Platform = (typeof PLATFORMS)[number]['id'];

interface StagedAccount {
  username: string;
  display_name: string;
  avatar_url: string;
  bio: string;
  platform: Platform;
  follower_count: number;
  video_count: number;
  like_count: number;
  is_verified: boolean;
}

const formatFollowerCount = (count: number | null | undefined): string => {
  const n = count || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'pending':  return <Badge variant="outline" className="text-yellow-600 border-yellow-600"><Clock className="h-3 w-3 mr-1" />Pending review</Badge>;
    case 'approved': return <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Linked</Badge>;
    case 'rejected': return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    default:         return null;
  }
};

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  if (platform === 'instagram') return <Instagram className="h-4 w-4" />;
  if (platform === 'youtube')   return <Youtube className="h-4 w-4" />;
  if (platform === 'tiktok')    return <span className="text-xs font-bold">TT</span>;
  return <User className="h-4 w-4" />;
};

export const SocialMediaLinksTab: React.FC = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedPlatform, setSelectedPlatform] = useState<Platform>('tiktok');
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [stagedAccounts, setStagedAccounts] = useState<StagedAccount[]>([]);

  // Live API search via search-creators edge function
  const { data: apiData, isLoading } = useCreators({
    platform: selectedPlatform,
    query: debouncedSearch,
    enabled: debouncedSearch.trim().length > 0,
  });
  const searchResults = apiData?.creators || [];

  // Already-linked / pending accounts for the current user
  const { data: myLinked, isLoading: linkedLoading } = useQuery({
    queryKey: ['my-social-links', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return [];
      const { data, error } = await supabase
        .from('creators')
        .select('id, username, display_name, avatar_url, platform, follower_count, claim_status, claimed_at')
        .eq('claimed_by_profile_id', currentUser.id)
        .in('claim_status', ['pending', 'approved', 'rejected'])
        .order('claimed_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentUser?.id,
  });

  const linkedKeySet = useMemo(
    () => new Set((myLinked || []).map((l: any) => `${l.platform}:${l.username}`)),
    [myLinked],
  );

  // Submit all staged accounts as pending claims
  const submitMutation = useMutation({
    mutationFn: async (accounts: StagedAccount[]) => {
      const results = [];
      for (const a of accounts) {
        const { data, error } = await supabase.functions.invoke('claim-creator-profile', {
          body: {
            platforms: { [a.platform]: a.username },
            displayName: a.display_name,
            bio: a.bio || '',
            avatarUrl: a.avatar_url,
            followerCount: a.follower_count,
            videoCount: a.video_count,
            likeCount: a.like_count,
            isVerified: a.is_verified,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        results.push(data);
      }
      return results;
    },
    onSuccess: () => {
      toast({
        title: stagedAccounts.length > 1 ? 'Accounts submitted' : 'Account submitted',
        description: 'Your social media link is pending admin review.',
      });
      setStagedAccounts([]);
      queryClient.invalidateQueries({ queryKey: ['my-social-links'] });
    },
    onError: (err: any) => {
      toast({
        title: 'Failed to submit',
        description: mapSupabaseError(err, 'Could not submit one or more accounts.'),
        variant: 'destructive',
      });
    },
  });

  // Cancel a pending or rejected request (releases the row back to unclaimed).
  // Routed through cancel-creator-claim edge function (service role) to avoid
  // an RLS quirk: the SELECT policy on `creators` keeps the row visible only
  // while claimed_by_profile_id matches the caller, so a client-side UPDATE
  // that nulls claimed_by_profile_id trips a 42501 error from the implicit
  // "row must remain visible after update" check, even though the UPDATE
  // policy's WITH CHECK explicitly allows the NULL assignment.
  const cancelMutation = useMutation({
    mutationFn: async (creatorId: string) => {
      const { data, error } = await supabase.functions.invoke('cancel-creator-claim', {
        body: { creatorId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      toast({ title: 'Removed' });
      queryClient.invalidateQueries({ queryKey: ['my-social-links'] });
    },
    onError: (err: any) => {
      toast({
        title: 'Failed to remove',
        description: mapSupabaseError(err, 'Could not remove this link.'),
        variant: 'destructive',
      });
    },
  });

  const stageAccount = (creator: any) => {
    const dupKey = `${creator.platform}:${creator.username}`;
    if (stagedAccounts.some((a) => `${a.platform}:${a.username}` === dupKey)) {
      toast({ title: 'Already in your list', description: `@${creator.username} on ${creator.platform} was already added.`, variant: 'destructive' });
      return;
    }
    if (linkedKeySet.has(dupKey)) {
      toast({ title: 'Already linked', description: `@${creator.username} on ${creator.platform} is already linked or pending review.`, variant: 'destructive' });
      return;
    }
    setStagedAccounts((prev) => [...prev, {
      username: creator.username,
      display_name: creator.display_name || creator.username,
      avatar_url: creator.avatar_url || '',
      bio: creator.bio || '',
      platform: creator.platform as Platform,
      follower_count: creator.follower_count || 0,
      video_count: creator.video_count || 0,
      like_count: creator.like_count || 0,
      is_verified: !!creator.is_verified,
    }]);
    setSearchTerm('');
  };

  return (
    <div className="space-y-6">
      {/* Currently linked / pending */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Your Linked Accounts
          </CardTitle>
          <CardDescription>
            Social profiles you have submitted. New submissions show up as <em>Pending review</em> until an admin approves them.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {linkedLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : !myLinked || myLinked.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              No social accounts linked yet. Search below to add your first one.
            </p>
          ) : (
            <div className="space-y-2">
              {myLinked.map((req: any) => (
                <div key={req.id} className="flex items-center gap-3 p-2 border rounded-md">
                  <Avatar className="h-9 w-9">
                    <SafeAvatarImage src={getProxiedImageUrl(req.avatar_url)} className="object-cover" />
                    <AvatarFallback>{(req.display_name || req.username)?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{req.display_name || req.username}</div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <PlatformIcon platform={req.platform} />
                      <span>@{req.username}</span>
                      {req.follower_count != null && (
                        <>
                          <span>•</span>
                          <span>{formatFollowerCount(req.follower_count)} followers</span>
                        </>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={req.claim_status} />
                  {(req.claim_status === 'pending' || req.claim_status === 'rejected') && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => cancelMutation.mutate(req.id)}
                      disabled={cancelMutation.isPending}
                      className="text-destructive h-8 w-8"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search + stage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Link a New Account</CardTitle>
          <CardDescription>
            Search for your handle on each platform, add the matching profile, then submit for review.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {PLATFORMS.map((p) => (
              <Button
                key={p.id}
                variant={selectedPlatform === p.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setSelectedPlatform(p.id); setSearchTerm(''); }}
                className="gap-1.5"
              >
                <p.Icon />
                <span>{p.label}</span>
              </Button>
            ))}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search your ${PLATFORMS.find((p) => p.id === selectedPlatform)?.label} username…`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && !debouncedSearch.trim() && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <Globe className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Type your username above to find your profile.
            </div>
          )}

          {!isLoading && debouncedSearch.trim().length > 0 && searchResults.length === 0 && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              No results for &quot;{debouncedSearch}&quot;.
            </div>
          )}

          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((creator) => {
                const key = `${creator.platform}:${creator.username}`;
                const isStaged = stagedAccounts.some((a) => `${a.platform}:${a.username}` === key);
                const isLinked = linkedKeySet.has(key);
                return (
                  <div key={key} className={`flex items-center p-3 gap-3 border rounded-md ${isStaged ? 'border-green-500 bg-green-50 dark:bg-green-950/20' : ''}`}>
                    <Avatar className="h-10 w-10">
                      <SafeAvatarImage src={getProxiedImageUrl(creator.avatar_url)} className="object-cover" />
                      <AvatarFallback>{(creator.display_name || creator.username)?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate flex items-center gap-1.5">
                        {creator.display_name || creator.username}
                        {creator.is_verified && <CheckCircle className="h-4 w-4 text-primary shrink-0" />}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <PlatformIcon platform={creator.platform} />
                        <span>@{creator.username}</span>
                        <span>•</span>
                        <span>{formatFollowerCount(creator.follower_count)} followers</span>
                      </div>
                    </div>
                    {isLinked ? (
                      <Badge variant="outline">Already linked</Badge>
                    ) : isStaged ? (
                      <Badge className="bg-green-600"><CheckCircle className="h-3 w-3 mr-1" />Added</Badge>
                    ) : (
                      <Button size="sm" onClick={() => stageAccount(creator)}>
                        <Plus className="h-3 w-3 mr-1" />Add
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Staged Summary */}
          {stagedAccounts.length > 0 && (
            <div className="border-t pt-4 space-y-3">
              <div className="text-sm font-medium">
                Ready to submit ({stagedAccounts.length})
              </div>
              {stagedAccounts.map((a, idx) => (
                <div key={`staged-${a.platform}-${a.username}`} className="flex items-center gap-3">
                  <Avatar className="h-7 w-7">
                    <SafeAvatarImage src={getProxiedImageUrl(a.avatar_url)} className="object-cover" />
                    <AvatarFallback>{(a.display_name || a.username)?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-sm">
                    <span className="font-medium">{a.display_name || a.username}</span>
                    <span className="text-xs text-muted-foreground ml-2">@{a.username} • {a.platform}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive h-8 w-8"
                    onClick={() => setStagedAccounts((prev) => prev.filter((_, i) => i !== idx))}
                    aria-label="Remove from staging"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                className="w-full"
                onClick={() => submitMutation.mutate(stagedAccounts)}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting…</>
                ) : (
                  <><Shield className="h-4 w-4 mr-2" />Submit for review</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
